/**
 * Геодезические метрики для инструмента измерения на карте (клиент, WGS-84).
 */

export type LngLat = [number, number];

const EARTH_RADIUS_M = 6_371_000;

export function haversineDistanceMeters(a: LngLat, b: LngLat): number {
  const dLat = ((b[1] - a[1]) * Math.PI) / 180;
  const dLon = ((b[0] - a[0]) * Math.PI) / 180;
  const lat1 = (a[1] * Math.PI) / 180;
  const lat2 = (b[1] * Math.PI) / 180;
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;
  return EARTH_RADIUS_M * 2 * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s));
}

export function pathLengthMeters(points: LngLat[]): number {
  let total = 0;
  for (let i = 1; i < points.length; i++) {
    total += haversineDistanceMeters(points[i - 1], points[i]);
  }
  return total;
}

/**
 * Площадь замкнутого кольца на сфере (формула spherical excess / OL-style).
 * Кольцо без обязательного дублирования первой точки в конце.
 */
export function ringAreaMeters2(ring: LngLat[]): number {
  if (ring.length < 3) return 0;
  let area = 0;
  const n = ring.length;
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    const xi = (ring[i][0] * Math.PI) / 180;
    const yi = (ring[i][1] * Math.PI) / 180;
    const xj = (ring[j][0] * Math.PI) / 180;
    const yj = (ring[j][1] * Math.PI) / 180;
    area += (xj - xi) * (2 + Math.sin(yi) + Math.sin(yj));
  }
  return Math.abs((area * EARTH_RADIUS_M * EARTH_RADIUS_M) / 2);
}

export function circleAreaMeters2(radiusMeters: number): number {
  return Math.PI * radiusMeters * radiusMeters;
}

export function circleCircumferenceMeters(radiusMeters: number): number {
  return 2 * Math.PI * radiusMeters;
}

/** Аппроксимация окружности как LineString (замкнутый). */
export function circleLineString(
  center: LngLat,
  radiusMeters: number,
  segments = 64,
): LngLat[] {
  const coords: LngLat[] = [];
  const [lng0, lat0] = center;
  const latRad = (lat0 * Math.PI) / 180;
  const dLat = radiusMeters / EARTH_RADIUS_M;
  const dLng = radiusMeters / (EARTH_RADIUS_M * Math.max(0.2, Math.cos(latRad)));
  for (let i = 0; i <= segments; i++) {
    const a = (i / segments) * Math.PI * 2;
    const lat = lat0 + ((dLat * Math.sin(a) * 180) / Math.PI);
    const lng = lng0 + ((dLng * Math.cos(a) * 180) / Math.PI);
    coords.push([lng, lat]);
  }
  return coords;
}

export function formatDistanceMeters(meters: number): string {
  if (meters >= 1000) return `${(meters / 1000).toFixed(2)} км`;
  return `${meters.toFixed(1)} м`;
}

/** Подпись HUD измерения: до 10 км — в метрах с сотыми (как в ГИС-референсах). */
export function formatHudDistanceMeters(meters: number): string {
  if (meters >= 10_000) return `${(meters / 1000).toFixed(2)} км`;
  return `${meters.toFixed(2)} м`;
}

export function formatAreaMeters2(area: number): string {
  if (area >= 1_000_000) return `${(area / 1_000_000).toFixed(2)} км²`;
  if (area >= 10_000) return `${(area / 10_000).toFixed(2)} га`;
  return `${area.toFixed(1)} м²`;
}

export function formatLngLat(lng: number, lat: number, digits = 7): string {
  return `${lat.toFixed(digits)}, ${lng.toFixed(digits)}`;
}

/** Прямой азимут a→b, градусы [0, 360), 0 = север, по часовой. */
export function bearingDegrees(a: LngLat, b: LngLat): number {
  const lat1 = (a[1] * Math.PI) / 180;
  const lat2 = (b[1] * Math.PI) / 180;
  const dLng = ((b[0] - a[0]) * Math.PI) / 180;
  const y = Math.sin(dLng) * Math.cos(lat2);
  const x =
    Math.cos(lat1) * Math.sin(lat2) -
    Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng);
  const brng = (Math.atan2(y, x) * 180) / Math.PI;
  return (brng + 360) % 360;
}

export function formatBearingDegrees(deg: number): string {
  return `${deg.toFixed(1)}°`;
}
